import React from 'react';

// ── Theme Constants ─────────────────────────────────────────────────────────
const T = {
  bg: '#0F1419',
  card: '#1A2332',
  cardHover: '#1E2A3A',
  accent: '#008751',
  gold: '#D4AF37',
  border: 'rgba(0,135,81,0.15)',
  border2: 'rgba(255,255,255,0.06)',
  text1: '#ffffff',
  text2: 'rgba(255,255,255,0.6)',
  text3: 'rgba(255,255,255,0.3)',
};

// ── PAGE HEADER ───────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{padding:'32px 32px 0',display:'flex',
      alignItems:'flex-start',justifyContent:'space-between',marginBottom:28}}>
      <div>
        <h1 style={{fontFamily:'Syne',fontSize:28,fontWeight:800,
          color:T.text1,letterSpacing:-0.5}}>{title}</h1>
        {subtitle && <p style={{fontSize:13,color:T.text3,marginTop:6}}>{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ── STAT CARD ─────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, icon, color = '#008751', trend, borderColor }) {
  const topBorder = borderColor || color;
  return (
    <div style={{
      background: T.card,
      border:`1px solid ${T.border2}`,
      borderTop:`3px solid ${topBorder}`,
      borderRadius:16,padding:24,position:'relative',overflow:'hidden',
      transition:'transform 0.2s, box-shadow 0.2s',
    }}>
      {/* Glow */}
      <div style={{position:'absolute',top:-30,right:-30,width:100,height:100,
        borderRadius:'50%',background:topBorder,opacity:0.06,filter:'blur(25px)'}} />
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
        <div>
          <div style={{fontSize:12,color:T.text3,
            letterSpacing:1,textTransform:'uppercase',marginBottom:8,fontWeight:500}}>{label}</div>
          <div style={{fontFamily:'Syne',fontSize:32,fontWeight:800,
            color:T.text1,lineHeight:1}}>{value}</div>
          {sub && <div style={{fontSize:12,color:T.text3,marginTop:8}}>{sub}</div>}
          {trend !== undefined && (
            <div style={{fontSize:12,color: trend >= 0 ? T.accent : '#ef4444',
              marginTop:8,display:'flex',alignItems:'center',gap:4,fontWeight:600}}>
              {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last week
            </div>
          )}
        </div>
        {icon && (
          <div style={{width:48,height:48,borderRadius:12,
            background:`${topBorder}18`,display:'flex',
            alignItems:'center',justifyContent:'center',
            border:`1px solid ${topBorder}30`,fontSize:22,flexShrink:0}}>{icon}</div>
        )}
      </div>
    </div>
  );
}

// ── CARD ──────────────────────────────────────────────────────────────────
export function Card({ children, title, action, style: s = {} }) {
  return (
    <div style={{background:T.card,border:`1px solid ${T.border2}`,
      borderRadius:16,...s}}>
      {(title || action) && (
        <div style={{padding:'18px 24px',borderBottom:`1px solid ${T.border2}`,
          display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          {title && <div style={{fontFamily:'Syne',fontSize:14,
            fontWeight:700,color:'rgba(255,255,255,0.85)',letterSpacing:0.3}}>{title}</div>}
          {action && <div>{action}</div>}
        </div>
      )}
      <div style={{padding:24}}>{children}</div>
    </div>
  );
}

// ── TABLE ─────────────────────────────────────────────────────────────────
export function Table({ columns, data, onRowClick, loading }) {
  if (loading) return (
    <div style={{padding:48,textAlign:'center',color:T.text3}}>
      <div style={{width:32,height:32,border:`2px solid ${T.accent}`,
        borderTopColor:'transparent',borderRadius:'50%',
        animation:'spin 0.8s linear infinite',margin:'0 auto 12px'}} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      Loading...
    </div>
  );
  if (!data?.length) return (
    <div style={{padding:48,textAlign:'center',color:T.text3,fontSize:13}}>
      No data found
    </div>
  );

  return (
    <div style={{overflowX:'auto'}}>
      <style>{`
        .admin-table{width:100%;border-collapse:collapse;}
        .admin-table th{padding:12px 16px;text-align:left;font-size:11px;
          letter-spacing:1px;text-transform:uppercase;color:rgba(255,255,255,0.35);
          font-family:'DM Mono',monospace;border-bottom:1px solid rgba(255,255,255,0.06);
          white-space:nowrap;background:rgba(0,135,81,0.04);}
        .admin-table td{padding:14px 16px;font-size:13px;color:rgba(255,255,255,0.8);
          border-bottom:1px solid rgba(255,255,255,0.04);}
        .admin-table tr:last-child td{border-bottom:none;}
        .admin-table tbody tr{transition:background 0.15s;}
        .admin-table tbody tr:hover{background:rgba(0,135,81,0.06);cursor:pointer;}
      `}</style>
      <table className="admin-table">
        <thead>
          <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} onClick={() => onRowClick?.(row)}>
              {columns.map(c => (
                <td key={c.key}>
                  {c.render ? c.render(row[c.key], row) : row[c.key] || '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── BADGE ─────────────────────────────────────────────────────────────────
export function Badge({ label, color }) {
  const colors = {
    free:     { bg:'rgba(255,255,255,0.08)', text:'rgba(255,255,255,0.5)' },
    student:  { bg:'rgba(0,135,81,0.15)',    text:'#008751' },
    premium:  { bg:'rgba(59,130,246,0.12)',  text:'#3b82f6' },
    scholar:  { bg:'rgba(212,175,55,0.18)',  text:'#D4AF37' },
    family:   { bg:'rgba(168,85,247,0.12)',  text:'#a855f7' },
    open:     { bg:'rgba(239,68,68,0.12)',   text:'#ef4444' },
    reviewed: { bg:'rgba(0,135,81,0.15)',    text:'#008751' },
    dismissed:{ bg:'rgba(255,255,255,0.06)', text:'rgba(255,255,255,0.3)' },
    flagged:  { bg:'rgba(239,68,68,0.2)',    text:'#ef4444' },
    active:   { bg:'rgba(0,135,81,0.15)',    text:'#008751' },
    paid:     { bg:'rgba(0,135,81,0.15)',    text:'#008751' },
    pending:  { bg:'rgba(212,175,55,0.15)',  text:'#D4AF37' },
    failed:   { bg:'rgba(239,68,68,0.12)',   text:'#ef4444' },
  };
  const c = colors[color || label?.toLowerCase()] || colors.free;
  return (
    <span style={{padding:'4px 12px',borderRadius:20,fontSize:11,
      fontWeight:600,background:c.bg,color:c.text,
      fontFamily:'DM Mono',letterSpacing:0.5,display:'inline-block'}}>
      {label}
    </span>
  );
}

// ── GREEN BUTTON (primary) ───────────────────────────────────────────────
export function GoldBtn({ children, onClick, small, danger, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: small ? '7px 16px' : '11px 22px',
      background: danger ? 'rgba(239,68,68,0.1)' : 'linear-gradient(135deg, #008751, #006B40)',
      border: danger ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(0,135,81,0.3)',
      borderRadius:10, color: danger ? '#ef4444' : '#fff',
      fontSize: small ? 12 : 14, fontWeight:700,
      fontFamily:'Syne',cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition:'all 0.2s',letterSpacing:0.3,
    }}>{children}</button>
  );
}

// ── GOLD BUTTON (secondary) ──────────────────────────────────────────────
export function GoldAccentBtn({ children, onClick, small }) {
  return (
    <button onClick={onClick} style={{
      padding: small ? '7px 16px' : '11px 22px',
      background:'rgba(212,175,55,0.12)',
      border:'1px solid rgba(212,175,55,0.3)',
      borderRadius:10, color:'#D4AF37',
      fontSize: small ? 12 : 14, fontWeight:700,
      fontFamily:'Syne',cursor:'pointer',
      transition:'all 0.2s',letterSpacing:0.3,
    }}>{children}</button>
  );
}

// ── LOADING SPINNER ───────────────────────────────────────────────────────
export function Spinner() {
  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:48,
      flexDirection:'column',gap:12}}>
      <div style={{width:36,height:36,border:`3px solid #008751`,
        borderTopColor:'transparent',borderRadius:'50%',
        animation:'spin 0.8s linear infinite'}} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <span style={{fontSize:12,color:T.text3,letterSpacing:1}}>Loading...</span>
    </div>
  );
}

// ── FILTER BAR ────────────────────────────────────────────────────────────
export function FilterBar({ filters, values, onChange }) {
  return (
    <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
      {filters.map(f => (
        <select key={f.key} value={values[f.key] || ''}
          onChange={e => onChange(f.key, e.target.value)}
          style={{padding:'9px 14px',background:T.card,
            border:`1px solid ${T.border2}`,borderRadius:10,
            color:'rgba(255,255,255,0.7)',fontSize:13,outline:'none',
            appearance:'none',cursor:'pointer',transition:'border-color 0.2s'}}>
          <option value="">{f.label}: All</option>
          {f.options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ))}
    </div>
  );
}

// ── SEARCH INPUT ──────────────────────────────────────────────────────────
export function SearchInput({ value, onChange, placeholder }) {
  return (
    <div style={{position:'relative'}}>
      <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',
        color:T.text3,fontSize:14}}>&#x1F50D;</span>
      <input value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Search...'}
        style={{padding:'10px 16px 10px 36px',background:T.card,
          border:`1px solid ${T.border2}`,borderRadius:10,
          color:'#fff',fontSize:13,outline:'none',width:260,
          transition:'border-color 0.2s'}} />
    </div>
  );
}

// ── TAB BUTTONS ───────────────────────────────────────────────────────────
export function TabButtons({ tabs, active, onChange }) {
  return (
    <div style={{display:'flex',gap:6,marginBottom:20,flexWrap:'wrap'}}>
      {tabs.map(tab => {
        const isActive = active === tab.value;
        return (
          <button key={tab.value} onClick={() => onChange(tab.value)}
            style={{padding:'8px 18px',borderRadius:10,border:'1px solid',
              borderColor: isActive ? '#008751' : 'rgba(255,255,255,0.08)',
              background:  isActive ? 'rgba(0,135,81,0.15)' : 'transparent',
              color:       isActive ? '#008751' : 'rgba(255,255,255,0.4)',
              fontSize:13,fontWeight:600,cursor:'pointer',
              transition:'all 0.2s',letterSpacing:0.3}}>
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span style={{marginLeft:8,padding:'2px 7px',
                background: isActive ? '#008751' : 'rgba(255,255,255,0.1)',
                borderRadius:10,color: isActive ? '#fff' : 'rgba(255,255,255,0.4)',
                fontSize:10,fontWeight:700}}>{tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
